import type {
  AccountsFilingStatusResponse,
  CalculateCt600Response,
  CompleteCt600Response,
  CreateCt600Payload,
  CreateCt600Response,
  Ct600BoxesResponse,
  Ct600DetailResponse,
  Ct600IxbrlResponse,
  Ct600ListResponse,
  Ct600PeriodSplitResponse,
  Ct600SectionPayload,
  Ct600SectionResponse,
  Ct600StatusResponse,
  Ct600SubmissionStatusResponse,
  Ct600SupplementaryDetailResponse,
  Ct600SupplementaryListResponse,
  Ct600SupplementaryRequirementResponse,
  DeleteSubmissionResponse,
  FileAccountsPayload,
  FileAccountsResponse,
  SubmitCt600Payload,
  SubmitCt600Response,
  UpdateCt600Payload,
  UpdateCt600Response,
  XmlArtifactsResponse,
} from '../types'
import { api } from './api'

export interface PeriodSplitOptions {
  start?: string
  end?: string
  /**
   * Skip the in-memory cache and re-hit Companies House. Use when the user
   * explicitly asks to refresh, e.g. clicks the Refresh button on Step 1.
   */
  forceRefresh?: boolean
}

// Period of account is stable per company — Companies House only changes
// `next_made_up_to` when accounts are filed. Cache the response per
// company+date triple to avoid re-hitting CH on every step navigation.
const PERIOD_SPLIT_TTL_MS = 30 * 60 * 1000
const periodSplitCache = new Map<
  string,
  { value: Ct600PeriodSplitResponse; expiresAt: number }
>()

// Backend strictly requires YYYY-MM-DD; callers occasionally pass full ISO
// timestamps (e.g. resumed drafts). Trim to the date portion.
const toDateOnly = (value?: string) => (value ? value.slice(0, 10) : undefined)

const periodSplitCacheKey = (
  companyNumber: string,
  options: PeriodSplitOptions
) =>
  `${companyNumber}|${toDateOnly(options.start) ?? ''}|${toDateOnly(options.end) ?? ''}`

class Ct600Service {
  private readonly basePath = '/filling/ct600'

  /**
   * Discover the company's period of account and whether it must be split into
   * two CT600 returns (Long Period of Account / LPOA). Optionally override the
   * start/end dates derived from Companies House.
   */
  async getPeriodSplit(
    companyNumber: string,
    options: PeriodSplitOptions = {}
  ): Promise<Ct600PeriodSplitResponse> {
    const cacheKey = periodSplitCacheKey(companyNumber, options)
    if (!options.forceRefresh) {
      const cached = periodSplitCache.get(cacheKey)
      if (cached && cached.expiresAt > Date.now()) {
        return cached.value
      }
    }

    const start = toDateOnly(options.start)
    const end = toDateOnly(options.end)
    const query = new URLSearchParams()
    if (start) query.append('start', start)
    if (end) query.append('end', end)
    const suffix = query.toString() ? `?${query.toString()}` : ''
    const response = await api.get<Ct600PeriodSplitResponse['response']>(
      `${this.basePath}/period-split/${companyNumber}${suffix}`
    )
    periodSplitCache.set(cacheKey, {
      value: response,
      expiresAt: Date.now() + PERIOD_SPLIT_TTL_MS,
    })
    return response
  }

  /** Drop every cached period-split entry. Used by tests / explicit reset. */
  clearPeriodSplitCache(): void {
    periodSplitCache.clear()
  }

  /**
   * Compute the AP1/AP2 split from user-entered accounting period dates with
   * NO Companies House lookup. Used when filingScope === 'ct_only' — typically
   * because accounts have already been filed elsewhere and CH's
   * `next_made_up_to` would point at a later period than the one HMRC has
   * open. The user enters the dates HMRC has on file and we just run the
   * split arithmetic against the backend.
   *
   * Not cached — input changes too often for caching to be useful.
   */
  async calculatePeriodSplit(payload: {
    start: string
    end: string
    companyNumber?: string
    utr?: string
  }): Promise<Ct600PeriodSplitResponse> {
    return await api.post<Ct600PeriodSplitResponse['response']>(
      `${this.basePath}/period-split`,
      {
        start: toDateOnly(payload.start) ?? payload.start,
        end: toDateOnly(payload.end) ?? payload.end,
        ...(payload.companyNumber
          ? { companyNumber: payload.companyNumber }
          : {}),
        ...(payload.utr ? { utr: payload.utr } : {}),
      }
    )
  }

  async listForms(
    params: Record<string, string | number | undefined> = {}
  ): Promise<Ct600ListResponse> {
    const query = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        query.append(key, String(value))
      }
    })

    const suffix = query.toString() ? `?${query.toString()}` : ''

    return await api.get<Ct600ListResponse['response']>(
      `${this.basePath}${suffix}`
    )
  }

  async getForm(formId: string): Promise<Ct600DetailResponse> {
    return await api.get<Ct600DetailResponse['response']>(
      `${this.basePath}/${formId}`
    )
  }

  async getFormStatus(formId: string): Promise<Ct600StatusResponse> {
    return await api.get<Ct600StatusResponse['response']>(
      `${this.basePath}/${formId}/get-form-status`
    )
  }

  async getCt600Boxes(formId: string): Promise<Ct600BoxesResponse> {
    return await api.get<Ct600BoxesResponse['response']>(
      `${this.basePath}/${formId}/ct600-boxes`
    )
  }

  async createForm(payload: CreateCt600Payload): Promise<CreateCt600Response> {
    return await api.post<CreateCt600Response['response']>(
      this.basePath,
      payload
    )
  }

  async updateForm(
    formId: string,
    payload: UpdateCt600Payload
  ): Promise<UpdateCt600Response> {
    return await api.put<UpdateCt600Response['response']>(
      `${this.basePath}/${formId}`,
      payload
    )
  }

  async calculate(
    formId: string,
    payload?: { userId?: string }
  ): Promise<CalculateCt600Response> {
    return await api.post<CalculateCt600Response['response']>(
      `${this.basePath}/${formId}/calculate`,
      payload
    )
  }

  async completeForm(
    formId: string,
    payload: { companyId?: string }
  ): Promise<CompleteCt600Response> {
    return await api.put<CompleteCt600Response['response']>(
      `${this.basePath}/${formId}/complete`,
      payload
    )
  }

  async patchSection(
    formId: string,
    section: string,
    payload: Ct600SectionPayload
  ): Promise<Ct600SectionResponse> {
    return await api.patch<Ct600SectionResponse['response']>(
      `${this.basePath}/${formId}/section/${section}`,
      payload
    )
  }

  async submitForm(
    formId: string,
    payload: SubmitCt600Payload
  ): Promise<SubmitCt600Response> {
    return await api.post<SubmitCt600Response['response']>(
      `${this.basePath}/${formId}/submit`,
      payload
    )
  }

  async getSubmissionStatus(
    formId: string
  ): Promise<Ct600SubmissionStatusResponse> {
    return await api.get<Ct600SubmissionStatusResponse['response']>(
      `${this.basePath}/${formId}/submission-status`,
      { skipAuthLogout: true }
    )
  }

  /** Whole-filing receipt, or one accounting period's receipt when `period` is given. */
  async downloadReceipt(formId: string, period?: 1 | 2): Promise<Blob> {
    const query = period ? `?period=${period}` : ''
    const result = await api.get<Blob>(`${this.basePath}/${formId}/receipt${query}`, {
      headers: { Accept: 'application/pdf' },
    })
    return result.response
  }

  /** HMRC's CT600 form filled in with one period's return — as filed, or a draft if not yet filed. */
  async downloadPeriodReturn(formId: string, period: 1 | 2): Promise<Blob> {
    const result = await api.get<Blob>(`${this.basePath}/${formId}/periods/${period}/return`, {
      headers: { Accept: 'application/pdf' },
    })
    return result.response
  }

  /** Tax computation (iXBRL HTML) for one period. */
  async downloadPeriodComputation(formId: string, period: 1 | 2): Promise<Blob> {
    const result = await api.get<Blob>(`${this.basePath}/${formId}/periods/${period}/computation`, {
      headers: { Accept: 'text/html' },
    })
    return result.response
  }

  /** Every document for one period as a zip: receipt, return as filed, HMRC acceptance, computation, accounts. */
  async downloadPeriodDocuments(formId: string, period: 1 | 2): Promise<Blob> {
    const result = await api.get<Blob>(`${this.basePath}/${formId}/periods/${period}/documents`, {
      headers: { Accept: 'application/zip' },
    })
    return result.response
  }

  async generateIxbrl(formId: string): Promise<Ct600IxbrlResponse> {
    const result = await api.get<Ct600IxbrlResponse>(
      `${this.basePath}/${formId}/generate-ixbrl`,
      {
        headers: { Accept: 'application/xml' },
      }
    )

    return result.response
  }

  // --------------------------------------------------------
  // Post-submission actions
  // --------------------------------------------------------

  async deleteSubmission(formId: string): Promise<DeleteSubmissionResponse> {
    return await api.post<DeleteSubmissionResponse['response']>(
      `${this.basePath}/${formId}/delete-submission`,
      {},
      { skipAuthLogout: true }
    )
  }

  async getXmlArtifacts(formId: string): Promise<XmlArtifactsResponse> {
    return await api.get<XmlArtifactsResponse['response']>(
      `${this.basePath}/${formId}/xml-artifacts`
    )
  }

  /** The XML artifacts zip, streamed directly (no longer a shared file URL). */
  async downloadXmlArtifactsZip(formId: string): Promise<Blob> {
    const result = await api.get<Blob>(`${this.basePath}/${formId}/xml-artifacts?format=zip`, {
      headers: { Accept: 'application/zip' },
    })
    return result.response
  }

  async fileAccounts(
    formId: string,
    payload: FileAccountsPayload
  ): Promise<FileAccountsResponse> {
    return await api.post<FileAccountsResponse['response']>(
      `${this.basePath}/${formId}/file-accounts`,
      payload
    )
  }

  async getAccountsFilingStatus(
    formId: string
  ): Promise<AccountsFilingStatusResponse> {
    return await api.get<AccountsFilingStatusResponse['response']>(
      `${this.basePath}/${formId}/accounts-filing-status`
    )
  }

  // --------------------------------------------------------
  // Supplementary pages
  // --------------------------------------------------------

  private supplementaryBase(formId: string): string {
    return `${this.basePath}/${formId}/supplementary-pages`
  }

  async listSupplementaryPages(
    formId: string
  ): Promise<Ct600SupplementaryListResponse> {
    return await api.get<Ct600SupplementaryListResponse['response']>(
      this.supplementaryBase(formId)
    )
  }

  async getSupplementaryPage(
    formId: string,
    pageType: string
  ): Promise<Ct600SupplementaryDetailResponse> {
    return await api.get<Ct600SupplementaryDetailResponse['response']>(
      `${this.supplementaryBase(formId)}/${pageType}`
    )
  }

  async upsertSupplementaryPage(
    formId: string,
    pageType: string,
    payload: Record<string, unknown>
  ): Promise<Ct600SupplementaryDetailResponse> {
    return await api.put<Ct600SupplementaryDetailResponse['response']>(
      `${this.supplementaryBase(formId)}/${pageType}`,
      payload
    )
  }

  async deleteSupplementaryPage(
    formId: string,
    pageType: string
  ): Promise<Ct600SupplementaryDetailResponse> {
    return await api.delete<Ct600SupplementaryDetailResponse['response']>(
      `${this.supplementaryBase(formId)}/${pageType}`
    )
  }

  async checkSupplementaryRequirements(
    formId: string
  ): Promise<Ct600SupplementaryRequirementResponse> {
    return await api.get<Ct600SupplementaryRequirementResponse['response']>(
      `${this.supplementaryBase(formId)}/requirements/check`
    )
  }
}

export const ct600Service = new Ct600Service()
export default ct600Service
